import React, { useState } from 'react';
import { IFeedbackSubmissionData } from '@root/types';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from '@material-ui/core';
import styled from 'styled-components';
import { Field, Form } from 'react-final-form';
import {
  CardBody,
  QuestionTypography,
} from '../../styles/CardTextAndContainers';
import handleErrors from '../../utils/errorHandler';

interface ISubmissionData {
  usrEmail: string;
  usrFeedback: string;
  usrContact: boolean;
}

const FeedbackForm = () => {
  const [submitted, setSubmitted] = useState<'submitted' | 'error' | null>(
    null,
  );

  const submitUserInput = async (usrData: ISubmissionData) => {
    const data: IFeedbackSubmissionData = {
      feedback: usrData.usrFeedback,
      email: usrData.usrEmail,
      contact: usrData.usrContact,
    };

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    };

    fetch('/api/feedback', requestOptions)
      .then((response) => {
        handleErrors(response);
        response.text();
      })
      .then(() => setSubmitted('submitted'))
      .catch((error) => {
        setSubmitted('error');
        console.log('error', error);
      });
  };

  const mustBeEmail = (value: string) =>
    value?.match(/\S+@\S+\.\S+/) ? undefined : 'Invalid email address';

  const charLimit = (value: string) => {
    return !value || value.length < 5;
  };

  if (submitted === 'error') {
    return (
      <CardBody>
        There was an error submitting your feedback. Please try again.
      </CardBody>
    );
  }
  if (submitted === 'submitted') {
    return (
      <CardBody>
        Thank you for your feedback. If you have asked us to contact you we will
        be in touch as soon as possible.
      </CardBody>
    );
  }

  return (
    <Form
      onSubmit={(formObj: ISubmissionData) => {
        submitUserInput(formObj);
      }}
    >
      {({ handleSubmit, valid }) => (
        <FormContainer onSubmit={handleSubmit}>
          <QuestionTypography>
            Is there anything more you would like us to know about the
            performance of your distributor?
          </QuestionTypography>
          <Field validate={charLimit} name="usrFeedback" initialValue="">
            {({ input: { onChange, value } }) => {
              return (
                <MainInput
                  inputProps={{
                    maxLength: 1000,
                    'data-testid': 'feedback-input',
                  }}
                  helperText={`${value.length}/1000`}
                  multiline
                  rowsMax="6"
                  onChange={onChange}
                  variant="filled"
                />
              );
            }}
          </Field>

          <Grid
            container
            alignItems="flex-end"
            justify="space-between"
            direction="row"
          >
            <Field validate={mustBeEmail} name="usrEmail" initialValue="">
              {({ input: { onChange } }) => (
                <EmailInput
                  inputProps={{ 'data-testid': 'email-input' }}
                  autoComplete="email"
                  onChange={onChange}
                  label="Your e-mail address"
                />
              )}
            </Field>
            <Button
              aria-label="submit-btn"
              disabled={!valid}
              type="submit"
              color="secondary"
              variant="contained"
            >
              Submit
            </Button>
          </Grid>
          <Field type="checkbox" name="usrContact" initialValue={false}>
            {({ input: { onChange } }) => (
              <FormControlLabel
                onChange={onChange}
                name="contact"
                control={
                  <Checkbox inputProps={{ 'aria-label': 'contact checkbox' }} />
                }
                label={
                  <DisclaimerTypography>
                    I would like to be contacted regarding my feedback
                  </DisclaimerTypography>
                }
              />
            )}
          </Field>
        </FormContainer>
      )}
    </Form>
  );
};

const DisclaimerTypography = styled.div`
  display: block;
  font-family: EB Garamond;
  font-style: italic;
  font-weight: normal;
  font-size: 12px;
  line-height: 1.5;
  align-items: center;
  letter-spacing: 0.15px;
`;

const EmailInput = styled(TextField)`
  width: 380px;
  margin: 25px 0px 0px 0px;
`;

const MainInput = styled(TextField)`
  width: 100%;
  margin: 25px 0px 0px 0px;
`;

const FormContainer = styled.form`
  max-width: 85%;
  min-height: 400px;
`;

export default FeedbackForm;
